import { API_TOKEN_RESOURCES } from '@/api'
import { LEVEL_RANK, type ScopeLevel } from './useScopeLevels'

/** The three starting points #628 offers: watching, operating, and full access. */
export type GrantPresetKey = 'watching' | 'operating' | 'full-access'

/**
 * One named starting point for granting a working set (#628): a small set of per-resource levels an owner
 * can apply in one action instead of setting twenty individually, and then adjust before it is sent. `levels`
 * is null for Full access, which maps to `isOwner: true` rather than any enumerated scope.
 */
export type GrantPreset = {
    key: GrantPresetKey
    label: string
    /** Offered only to an inviter who is themself an owner: nobody short of one can hand out everything. */
    ownerOnly: boolean
    levels: Record<string, ScopeLevel> | null
}

/**
 * The day-to-day operational set: everything somebody actually running the work has to be able to change.
 *
 * The owner/administrative surfaces (`enterprise`, `members`, `roles`, `spend`) are deliberately not in it —
 * an observer cannot see them at all, and somebody running the work reads them without being able to change
 * who may reach the enterprise or what it spends. Either can still be set by hand afterwards: a preset is a
 * starting point, not a cage.
 */
const OPERATING_RESOURCES = [
    'blockers',
    'catalog',
    'chats',
    'executions',
    'gates',
    'kpis',
    'memory',
    'plans',
    'proposals',
    'recommendations',
    'runs',
    'tasks',
    'teams',
    'tools',
    'webhooks',
]

/** Every base resource at `level`, the same shape `useScopeLevels` starts a picker in. */
export function everyResourceAt(level: ScopeLevel): Record<string, ScopeLevel> {
    return Object.fromEntries(API_TOKEN_RESOURCES.map(resource => [resource, level])) as Record<string, ScopeLevel>
}

/** `base` for every resource, then `level` for each of `resources`: how each preset below is written out. */
function levelsWith(base: ScopeLevel, level: ScopeLevel, resources: string[]): Record<string, ScopeLevel> {
    const levels = everyResourceAt(base)
    for (const resource of resources) {
        levels[resource] = level
    }
    return levels
}

/**
 * Resources no preset reaches, not even to read: billing (os#1615) is the owner's until a person is granted it by
 * name, so somebody operating does not see the plan, card or invoices just because they read the other admin
 * surfaces; mail (#883) is a person's own mailbox, which they reach whatever their grant, so a grant never names it.
 */
const NAMED_GRANT_ONLY = ['billing', 'mail']

/** Each of `resources` at `none`. */
function levelsWithout(resources: string[]): Record<string, ScopeLevel> {
    return Object.fromEntries(resources.map(resource => [resource, 'none'])) as Record<string, ScopeLevel>
}

/**
 * Somebody who watches: `read` on every resource except the owner/administrative surfaces, which they cannot
 * see at all. Built from the same operational set Operating writes to, plus `llm` and `orgs` — the two
 * remaining resources that are neither operational nor an owner/administrative surface — so a resource added
 * to the operational set is picked up here too instead of risking two lists drifting apart. Enumerated rather
 * than "everything but", so adding a genuinely new base resource does not silently widen what an observer
 * already granted — it starts at `none` here until it is named in one of these lists.
 */
const WATCHING_LEVELS = levelsWith('none', 'read', [...OPERATING_RESOURCES, 'llm', 'orgs'])

/**
 * Somebody who runs things: `write` across the day-to-day operational set, and `read` on the rest — including
 * the administrative surfaces, which they can see the state of without being able to change who may reach the
 * enterprise or what it spends.
 */
const OPERATING_LEVELS = { ...levelsWith('read', 'write', OPERATING_RESOURCES), ...levelsWithout(NAMED_GRANT_ONLY) }

/**
 * The presets a grant editor offers (#628): Watching, Operating, and Full access — the three common ways
 * somebody joins an enterprise. Read from here rather than re-declared, so a picker and anything else that
 * ever offers the same starting points cannot drift into two different ideas of what "Operating" grants.
 */
export const GRANT_PRESETS: GrantPreset[] = [
    { key: 'watching', label: 'Watching', ownerOnly: false, levels: WATCHING_LEVELS },
    { key: 'operating', label: 'Operating', ownerOnly: false, levels: OPERATING_LEVELS },
    // Owning the enterprise is the whole grant, so Full access enumerates nothing: it maps to `isOwner: true`,
    // and only an inviter who is themself an owner may hand it out.
    { key: 'full-access', label: 'Full access', ownerOnly: true, levels: null },
]

/** The narrower of two levels. */
function narrower(a: ScopeLevel, b: ScopeLevel): ScopeLevel {
    return LEVEL_RANK[a] <= LEVEL_RANK[b] ? a : b
}

/**
 * What `preset` actually grants once it is capped at no more than `heldLevels` allows — choosing a preset
 * must never grant more than the inviter holds (#628) — unless `heldIsOwner`, who is capped by nothing.
 *
 * Ownership, not what is enumerated, is what exempts an inviter: an owner's own grant enumerates no scopes at
 * all (`EnterpriseHuman.scopes` is null for an owner), so clamping them against their own `heldLevels` would
 * cap every preset at `none`. A preset that enumerates nothing — Full access, which is `isOwner: true` —
 * proposes no per-resource levels either, so there is nothing here to clamp.
 */
export function applyPresetLevels(
    preset: GrantPreset,
    heldLevels: Record<string, ScopeLevel>,
    heldIsOwner: boolean,
): Record<string, ScopeLevel> {
    if (!preset.levels) {
        return {}
    }
    if (heldIsOwner) {
        return { ...preset.levels }
    }
    return Object.fromEntries(
        Object.entries(preset.levels).map(([resource, level]) => [
            resource,
            narrower(level, heldLevels[resource] ?? 'none'),
        ]),
    ) as Record<string, ScopeLevel>
}

/**
 * Whether `current` has drifted from `presetLevels`, the levels the chosen preset itself produced — so a
 * grant editor can say when it has been adjusted away from the preset that started it. Null `presetLevels`
 * means no preset produced `current`, which is never itself a drift. Compared against the levels as applied
 * (already clamped), not the preset's own table, so an inviter who holds less than a preset asks for is not
 * told their grant is "customized" the moment they choose one.
 */
export function isCustomizedFromPreset(
    current: Record<string, ScopeLevel>,
    presetLevels: Record<string, ScopeLevel> | null,
): boolean {
    if (!presetLevels) {
        return false
    }
    const resources = new Set([...Object.keys(presetLevels), ...Object.keys(current)])
    for (const resource of resources) {
        if ((current[resource] ?? 'none') !== (presetLevels[resource] ?? 'none')) {
            return true
        }
    }
    return false
}
